import { useThree } from "@react-three/fiber";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { OrbitControls } from '@react-three/drei';
import { useVehicle, useAssistance } from "../../hooks";
import { gsap } from "gsap"
import { Human } from "../common/3d/Human";
import { Jaguar, jaguarOccupantPositions, jaguarCameraPositions } from "../common/3d/Jaguar";
import { VWID4, vwID4OccupantPositions, vwID4CameraPositions } from "../common/3d/VWID4";
import { Rafale, rafaleOccupantPositions, rafaleCameraPositions } from "../common/3d/Rafale";



const getCameraPositions = (vin) => {
  if (vin?.startsWith("SAD")) return jaguarCameraPositions;
  if (vin?.startsWith("WV")) return vwID4CameraPositions;
  if (vin?.startsWith("VF")) return rafaleCameraPositions;
  return rafaleCameraPositions; // fallback
}

const getOccupantPositions = (vin) => {
  // console.log("VIN in getOccupantPositions:", vin);
  if (vin?.startsWith("SAD")) return jaguarOccupantPositions;
  if (vin?.startsWith("WV")) return vwID4OccupantPositions;
  if (vin?.startsWith("VF")) return rafaleOccupantPositions;
  return rafaleOccupantPositions; // fallback
};


/**
 * Renders the scene viewer component
 * 
 * @returns the scene viewer component
 */
const SceneViewer = () => {
  const { vehicle } = useVehicle();
  const {assistance} = useAssistance();
  const { camera } = useThree();

  const [displayCrash, setDisplayCrash] = useState(false);
  const occupantPositions = getOccupantPositions(vehicle?.Vehicle?.VehicleIdentification?.VIN);
  const cameraPositions = getCameraPositions(vehicle?.Vehicle?.VehicleIdentification?.VIN);

  /**
   * Initializes the camera
   * 
   * @param {Object} camera - The camera object
   */
  const initializeCamera = useCallback((camera) => {
    camera.position.set(cameraPositions.initialPosition[0], cameraPositions.initialPosition[1], cameraPositions.initialPosition[2]);
    camera.fov = 30;
    camera.updateProjectionMatrix();
  }, [cameraPositions]);

  /**
   * Animates the engine
   * 
   * @param {Object} camera - The camera object
   */
  const engine = useCallback((camera) => {
    const tl = gsap.timeline();
    tl
      .to(camera.position, {
        duration: 3,
      })
      .to(camera.position, {
        x: cameraPositions.frontPosition[0],
        y: cameraPositions.frontPosition[1],
        z: cameraPositions.frontPosition[2],
        duration: 5,
        ease: "power4.inOut"
      });
  }, [cameraPositions]);

  /**
   * Animates the front left tire
   * 
   * @param {Object} camera - The camera object
   */
  const frontLeftTire = useCallback((camera) => {
    const tl = gsap.timeline();
    tl
      .to(camera.position, {
        duration: 3,
      })
      .to(camera.position, {
        x: -5,
        y: 1,
        z: -4,
        duration: 2,
        ease: "power4.inOut",
      });
  }, []);

  /**
   * Animates the front right tire
   * 
   * @param {Object} camera - The camera object
   */
  const frontRightTire = useCallback((camera) => {
    const tl = gsap.timeline();
    tl
      .to(camera.position, {
        duration: 3,
      })
      .to(camera.position, {
        x: 5,
        y: 1,
        z: -4,
        duration: 2,
        ease: "power4.inOut",
      });
  }, []);

  /**
   * Animates the rear left tire
   * 
   * @param {Object} camera - The camera object
   */
  const rearLeftTire = useCallback((camera) => {
    const tl = gsap.timeline();
    tl
      .to(camera.position, {
        duration: 3,
      })
      .to(camera.position, {
        x: -5,
        y: 1,
        z: 4,
        duration: 2,
        ease: "power4.inOut",
      });
  }, []);

  /**
   * Animates the rear right tire
   * 
   * @param {Object} camera - The camera object
   */
  const rearRightTire = useCallback((camera) => {
    const tl = gsap.timeline();
    tl
      .to(camera.position, {
        duration: 3,
      })
      .to(camera.position, {
        x: 5,
        y: 1,
        z: 4,
        duration: 2,
        ease: "power4.inOut",
      });
  }, []);

  const cameraMovements = useMemo(() =>
    ({ initializeCamera, engine, frontLeftTire, frontRightTire, rearLeftTire, rearRightTire }),
    [initializeCamera, engine, frontLeftTire, frontRightTire, rearLeftTire, rearRightTire]);

  /**
   * Gets the breakdown category
   * 
   * @returns The breakdown category
   */
  const getBreakdownCategory = useCallback(() => {
    if (vehicle?.Vehicle?.Powertrain?.CombustionEngine?.EOT >= 120) {
      return "engine";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Tire?.IsPressureLow) {
      return "frontLeftTireLowPressure";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Tire?.IsPressureLow) {
      return "frontRightTireLowPressure";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Tire?.IsPressureLow) {
      return "rearLeftTireLowPressure";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Tire?.IsPressureLow) {
      return "rearRightTireLowPressure";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Brake?.IsBrakesWorn) {
      return "frontLeftTireBrakeWorn";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Brake?.IsBrakesWorn) {
      return "frontRightTireBrakeWorn";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Brake?.IsBrakesWorn) {
      return "rearLeftTireBrakeWorn";
    } else if (vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Brake?.IsBrakesWorn) {
      return "rearRightTireBrakeWorn";
    }
    return null;
  }, [
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Left?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row1?.Wheel?.Right?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Left?.Tire?.IsPressureLow, vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Brake?.IsBrakesWorn,
    vehicle?.Vehicle?.Chassis?.Axle?.Row2?.Wheel?.Right?.Tire?.IsPressureLow,
    vehicle?.Vehicle?.Powertrain?.CombustionEngine?.EOT
  ]);

  
  useEffect(() => {
    if (assistance?.category === "ecall") {
      setDisplayCrash(true);  
    } else {
      setDisplayCrash(false);
    }
  }, [assistance?.category]); 


  useEffect(() => {

    const breakdownCategory = getBreakdownCategory();

    cameraMovements.initializeCamera(camera);

    switch (breakdownCategory) {
      case "engine":
        cameraMovements.engine(camera);
        break;
      case "battery":
        cameraMovements.engine(camera);
        break;
      case "frontLeftTireLowPressure":
        break;
      case "frontLeftTireBrakeWorn":
        cameraMovements.frontLeftTire(camera);
        break;
      case "frontRightTireLowPressure":
        break;
      case "frontRightTireBrakeWorn":
        cameraMovements.frontRightTire(camera);
        break;
      case "rearLeftTireLowPressure":
        break;
      case "rearLeftTireBrakeWorn":
        cameraMovements.rearLeftTire(camera);
        break;
      case "rearRightTireLowPressure":
      case "rearRightTireBrakeWorn":
        cameraMovements.rearRightTire(camera);
        break;
      default:
        cameraMovements.engine(camera);
        break
    }

  }, [camera, cameraMovements, getBreakdownCategory]);


  return (
    <React.Fragment >
      <OrbitControls maxPolarAngle={Math.PI / 2} />
            {vehicle?.Vehicle?.VehicleIdentification?.VIN?.startsWith("SAD") ? (
                <Jaguar vehicle={vehicle} isTransparent={true} isCrashed={displayCrash}  />
            ) : vehicle?.Vehicle?.VehicleIdentification?.VIN?.startsWith("WV") ? (
                <VWID4 vehicle={vehicle}  isTransparent={true} isCrashed={displayCrash} />
            ) : vehicle?.Vehicle?.VehicleIdentification?.VIN?.startsWith("VF") ? (
                <Rafale vehicle={vehicle} isTransparent={true} isCrashed={displayCrash}  />
            ) : (
                <Rafale vehicle={vehicle } isTransparent={true} isCrashed={displayCrash} />
            )}

      {vehicle?.Vehicle?.Occupant?.Row1?.DriverSide?.IsOccupied && (
        <Human activity={vehicle?.Vehicle?.Occupant?.Row1?.DriverSide?.Activity} position={occupantPositions?.Row1?.DriverSide} isCrashed={displayCrash}> </Human>
      )}

      {vehicle?.Vehicle?.Occupant?.Row1?.PassengerSide?.IsOccupied && (
        <Human activity={vehicle?.Vehicle?.Occupant?.Row1?.PassengerSide?.Activity} position={occupantPositions?.Row1?.PassengerSide} isCrashed={displayCrash}  ></Human>
      )}

      {vehicle?.Vehicle?.Occupant?.Row2?.DriverSide?.IsOccupied && (
        <Human activity={vehicle?.Vehicle?.Occupant?.Row2?.DriverSide?.Activity} position={occupantPositions?.Row2?.DriverSide} isCrashed={displayCrash} ></Human>
      )}

      {vehicle?.Vehicle?.Occupant?.Row2?.PassengerSide?.IsOccupied && (
        <Human activity={vehicle?.Vehicle?.Occupant?.Row2?.PassengerSide?.Activity} position={occupantPositions?.Row2?.PassengerSide} isCrashed={displayCrash} ></Human>
      )}

      {vehicle?.Vehicle?.Occupant?.Row2?.Middle?.IsOccupied && (
        <Human activity={vehicle?.Vehicle?.Occupant?.Row2?.Middle?.Activity} position={occupantPositions?.Row2?.Middle} isCrashed={displayCrash} ></Human>
      )}
      <ambientLight args={[0xffffff, 5]} />
      <directionalLight args={[0xffffff, 2]} position={[0, 0, 5]} />
      <directionalLight args={[0xffffff, 2]} position={[0, 0, -5]} />
      <directionalLight args={[0xffffff, 2]} position={[0, 5, 0]} />
      <directionalLight args={[0xffffff, 2]} position={[-5, 0, -5]} />
      <directionalLight args={[0xffffff, 2]} position={[5, 0, -5]} />

    </React.Fragment>
  );
}

export default React.memo(SceneViewer)